(function (root, factory) {
  const api = factory();
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }
  root.CustomBundleBuilder = api;
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  const EVENT_NAME = "custom-bundle:added";
  const FETCH_CONCURRENCY = 4;
  const MAX_RETRIES = 2;
  const CART_SECTIONS = ["cart-drawer", "cart-icon-bubble", "cart-notification"];
  const PASSWORD_HINT =
    "Storefront locked or product unavailable. On a development store, enter the store password at /password, or rely on Liquid-embedded prices.";

  const productCache = new Map();
  const inflight = new Map();
  let activeFetches = 0;
  const queue = [];
  let variantListboxDocumentOutsideBound = false;

  function parseConfig(raw) {
    if (!raw) {
      return null;
    }
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.error("[custom-bundle] invalid config JSON", error);
        return null;
      }
    }
    if (typeof raw === "object") {
      return raw;
    }
    return null;
  }

  function moneyFromCents(cents, locale, currency) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: "currency",
        currency: currency || "USD",
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  function bundleTotalLabel(cents, locale, currencyCode) {
    const amount = Number(cents || 0) / 100;
    const code = currencyCode || "USD";
    try {
      const parts = new Intl.NumberFormat(locale || undefined, {
        style: "currency",
        currency: code,
        currencyDisplay: "narrowSymbol",
      }).formatToParts(amount);
      const symbol = parts.find((part) => part.type === "currency")?.value || "";
      const number = parts
        .filter((part) => part.type !== "currency")
        .map((part) => part.value)
        .join("")
        .trim();
      return `${symbol}${number} ${code}`.replace(/\s+/g, " ").trim();
    } catch (error) {
      return `$${amount.toFixed(2)} ${code}`;
    }
  }

  function variantNumericId(gidOrId) {
    if (gidOrId == null) {
      return "";
    }
    const value = String(gidOrId);
    return value.includes("/") ? value.split("/").pop() : value;
  }

  function createInstanceId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `bundle_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  function allowedVariantIdSet(slot) {
    const ids = new Set();
    (slot.allowedProducts || []).forEach((product) => {
      (product.allowedVariantIds || []).forEach((id) => ids.add(variantNumericId(id)));
    });
    return ids;
  }

  function runQueue() {
    while (activeFetches < FETCH_CONCURRENCY && queue.length > 0) {
      const job = queue.shift();
      activeFetches += 1;
      job()
        .catch((error) => {
          throw error;
        })
        .finally(() => {
          activeFetches -= 1;
          runQueue();
        });
    }
  }

  function looksLikePasswordHtml(text) {
    if (!text || typeof text !== "string") {
      return false;
    }
    const sample = text.slice(0, 2000).toLowerCase();
    return sample.includes("password") && (sample.includes("store is password protected") || sample.includes('name="password"'));
  }

  function fetchProductJson(handle) {
    if (productCache.has(handle)) {
      return Promise.resolve(productCache.get(handle));
    }
    if (inflight.has(handle)) {
      return inflight.get(handle);
    }
    const request = new Promise((resolve, reject) => {
      const start = () => {
        fetch(`/products/${encodeURIComponent(handle)}.js`, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        })
          .then(async (response) => {
            const contentType = response.headers.get("content-type") || "";
            const bodyText = await response.text();
            if (!response.ok) {
              if (looksLikePasswordHtml(bodyText) || response.status === 401 || response.status === 403) {
                throw new Error(`password_gate:${handle}`);
              }
              throw new Error(`Product ${handle} failed with ${response.status}`);
            }
            if (looksLikePasswordHtml(bodyText) || !contentType.includes("json")) {
              try {
                const json = JSON.parse(bodyText);
                productCache.set(handle, json);
                inflight.delete(handle);
                resolve(json);
                return;
              } catch (error) {
                throw new Error(`password_gate:${handle}`);
              }
            }
            try {
              const json = JSON.parse(bodyText);
              productCache.set(handle, json);
              inflight.delete(handle);
              resolve(json);
            } catch (error) {
              throw new Error(`Product ${handle} returned invalid JSON`);
            }
          })
          .catch((error) => {
            inflight.delete(handle);
            reject(error);
          });
      };
      queue.push(async () => start());
      runQueue();
    });
    inflight.set(handle, request);
    return request;
  }

  function filterVariants(productJson, allowedIds) {
    return (productJson.variants || []).filter((variant) => allowedIds.has(String(variant.id)));
  }

  function isSoldOut(variant) {
    return variant?.available === false;
  }

  function parseCartAddError(bodyText) {
    if (!bodyText) {
      return "Could not add this bundle. Try again.";
    }
    try {
      const parsed = JSON.parse(bodyText);
      const detail = parsed.description || parsed.message || parsed.error;
      if (typeof detail === "string" && detail.trim()) {
        return `Could not add this bundle: ${detail.trim()}`;
      }
    } catch (error) {
      const trimmed = String(bodyText).replace(/\s+/g, " ").trim();
      if (trimmed) {
        return `Could not add this bundle: ${trimmed.slice(0, 280)}`;
      }
    }
    return "Could not add this bundle. Try again.";
  }

  function slotSelectionIssues(slot, quantityByVariantId) {
    const total = Object.values(quantityByVariantId).reduce((sum, qty) => sum + Number(qty || 0), 0);
    if (slot.required && total < Math.max(slot.min, 1)) {
      return `${slot.label} requires at least ${Math.max(slot.min, 1)}.`;
    }
    if (total < slot.min) {
      return `${slot.label} needs at least ${slot.min}.`;
    }
    if (total > slot.max) {
      return `${slot.label} allows at most ${slot.max}.`;
    }
    return "";
  }

  function validateSelections(config, selections) {
    const issues = [];
    (config.slots || []).forEach((slot) => {
      const issue = slotSelectionIssues(slot, selections[slot.id] || {});
      if (issue) {
        issues.push(issue);
      }
    });
    return issues;
  }

  function componentSumCents(config, selections, variantPrices) {
    let total = 0;
    (config.slots || []).forEach((slot) => {
      const qtyMap = selections[slot.id] || {};
      Object.entries(qtyMap).forEach(([variantId, quantity]) => {
        const price = variantPrices[variantId];
        if (typeof price === "number") {
          total += price * Number(quantity || 0);
        }
      });
    });
    const discount = Number(config.pricing?.percentageDecrease || 0);
    if (discount > 0) {
      total = Math.round(total * (1 - discount / 100));
    }
    return total;
  }

  function bundleLineProperties(config, instanceId, extra) {
    const properties = {
      _bundle_instance: instanceId,
      _bundle_key: config.bundleKey || "",
      _bundle_parent_variant_id: String(config.parentVariantId || ""),
      _bundle_config_version: String(config.configVersion || ""),
    };
    if (extra && typeof extra === "object") {
      Object.keys(extra).forEach((key) => {
        properties[key] = extra[key];
      });
    }
    return properties;
  }

  function buildCartPayload(config, selections, instanceId) {
    const items = [];
    const parentNumeric = variantNumericId(config.parentVariantId);
    (config.slots || []).forEach((slot) => {
      const qtyMap = selections[slot.id] || {};
      Object.entries(qtyMap).forEach(([variantId, quantity]) => {
        const qty = Number(quantity || 0);
        const id = Number(variantNumericId(variantId));
        if (qty < 1 || !id) {
          return;
        }
        if (parentNumeric && String(id) === parentNumeric) {
          return;
        }
        items.push({
          id,
          quantity: qty,
          properties: bundleLineProperties(config, instanceId, { _bundle_slot_id: slot.id }),
        });
      });
    });
    return { items };
  }

  async function postCart(payload, attempt = 0) {
    if (!payload.items || payload.items.length === 0) {
      throw new Error("empty_payload");
    }
    const response = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...payload,
        sections: CART_SECTIONS,
        sections_url: typeof window !== "undefined" ? window.location.pathname : "/",
      }),
    });
    const contentType = response.headers.get("content-type") || "";
    const bodyText = await response.text();
    if ((response.status === 429 || response.status >= 500) && attempt < MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
      return postCart(payload, attempt + 1);
    }
    if (looksLikePasswordHtml(bodyText) || response.status === 401 || response.status === 403) {
      throw new Error("password_gate:cart");
    }
    if (!response.ok) {
      const err = new Error(parseCartAddError(bodyText));
      err.cause = bodyText;
      throw err;
    }
    if (!contentType.includes("json") && looksLikePasswordHtml(bodyText)) {
      throw new Error("password_gate:cart");
    }
    try {
      return JSON.parse(bodyText);
    } catch (error) {
      if (looksLikePasswordHtml(bodyText)) {
        throw new Error("password_gate:cart");
      }
      throw new Error("Add to cart returned invalid JSON.");
    }
  }

  function getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const node = doc.querySelector(selector) || doc.querySelector(".shopify-section") || doc.body?.firstElementChild;
    return node ? node.innerHTML : html;
  }

  function replaceSectionHTML(elementId, html, selector) {
    const target =
      document.getElementById(elementId) ||
      document.querySelector(`.shopify-section-header-wrapper #${elementId}`);
    if (!target || !html) {
      return;
    }
    target.innerHTML = getSectionInnerHTML(html, selector || `#${elementId}`);
  }

  function refreshThemeCart(result) {
    try {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh"));
      if (typeof window !== "undefined" && window.theme?.cartUpdate) {
        window.theme.cartUpdate();
      }
      const sections = result?.sections || {};
      if (sections["cart-icon-bubble"]) {
        replaceSectionHTML("cart-icon-bubble", sections["cart-icon-bubble"], ".shopify-section");
      }
      const drawer = document.querySelector("cart-drawer");
      if (drawer && typeof drawer.renderContents === "function" && sections["cart-drawer"]) {
        try {
          drawer.classList.remove("is-empty");
          drawer.querySelector(".drawer__inner")?.classList.remove("is-empty");
          drawer.renderContents(result);
        } catch (error) {
          console.error("[custom-bundle] cart drawer refresh failed", error);
        }
      }
    } catch (error) {
      console.error("[custom-bundle] cart refresh failed", error);
    }
  }

  function filterParentOptions(config, options) {
    const parentNumeric = variantNumericId(config.parentVariantId);
    return (options || []).filter((option) => String(option.variant?.id) !== parentNumeric);
  }

  function catalogHasOptions(catalog) {
    return Array.isArray(catalog) && catalog.some((entry) => (entry.options || []).length > 0);
  }

  function catalogFromEmbedded(config, embedded) {
    if (!Array.isArray(embedded) || embedded.length === 0) {
      return null;
    }
    const variantPrices = {};
    const bySlotId = new Map(embedded.map((entry) => [String(entry.slotId), entry]));
    const catalog = (config.slots || []).map((slot) => {
      const entry = bySlotId.get(String(slot.id));
      const options = filterParentOptions(
        config,
        (entry?.options || []).map((option) => {
          const variantId = String(option.variant?.id ?? "");
          if (variantId && typeof option.variant?.price === "number") {
            variantPrices[variantId] = Number(option.variant.price);
          }
          return {
            productTitle: option.productTitle,
            handle: option.handle,
            featuredImage: option.featuredImage,
            galleryImage: option.galleryImage,
            categoryLabel: option.categoryLabel,
            pickerLabel: option.pickerLabel,
            productDescription: option.productDescription,
            productDescriptionHtml: option.productDescriptionHtml,
            ingredientsHtml: option.ingredientsHtml,
            productUrl: option.productUrl,
            variant: {
              id: option.variant?.id,
              title: option.variant?.title,
              price: option.variant?.price,
              available: option.variant?.available !== false,
              featured_image: option.variant?.featured_image || null,
            },
          };
        }),
      );
      return { slot, options };
    });
    if (!catalogHasOptions(catalog)) {
      return null;
    }
    return { catalog, variantPrices, failedHandles: [], passwordBlocked: false };
  }

  async function loadSlotCatalog(config) {
    const catalog = [];
    const handles = [];
    (config.slots || []).forEach((slot) => {
      (slot.allowedProducts || []).forEach((product) => {
        if (product.productHandle && !handles.includes(product.productHandle)) {
          handles.push(product.productHandle);
        }
      });
    });

    const settled = await Promise.allSettled(handles.map((handle) => fetchProductJson(handle)));
    const byHandle = new Map();
    const failedHandles = [];
    let passwordBlocked = false;
    settled.forEach((result, index) => {
      const handle = handles[index];
      if (result.status === "fulfilled") {
        byHandle.set(handle, result.value);
        return;
      }
      failedHandles.push(handle);
      const message = result.reason instanceof Error ? result.reason.message : String(result.reason || "");
      if (message.startsWith("password_gate:")) {
        passwordBlocked = true;
      }
      console.error("[custom-bundle] product.js failed", handle, result.reason);
    });

    const variantPrices = {};
    (config.slots || []).forEach((slot) => {
      const allowedIds = allowedVariantIdSet(slot);
      const options = [];
      (slot.allowedProducts || []).forEach((product) => {
        const json = byHandle.get(product.productHandle);
        if (!json) {
          return;
        }
        filterVariants(json, allowedIds).forEach((variant) => {
          variantPrices[String(variant.id)] = Number(variant.price);
          options.push({
            productTitle: json.title,
            handle: json.handle,
            productUrl: json.url,
            productDescription: typeof json.description === "string" ? json.description.replace(/<[^>]*>/g, "").slice(0, 120) : "",
            featuredImage: json.featured_image,
            galleryImage: variant.featured_image?.src || json.featured_image,
            variant,
          });
        });
      });
      catalog.push({ slot, options: filterParentOptions(config, options) });
    });

    return { catalog, variantPrices, failedHandles, passwordBlocked };
  }

  function optionImage(option) {
    const src = option.variant.featured_image?.src || option.featuredImage;
    if (!src) {
      return "";
    }
    return `<img src="${src}" alt="${escapeHtml(option.productTitle)}" width="128" height="128" loading="lazy">`;
  }

  function optionGalleryImage(option) {
    return option.galleryImage || option.variant?.featured_image?.src || option.featuredImage || "";
  }

  function formatTitleLine(option) {
    return option.productTitle || "";
  }

  function updateBundleGalleryImage(sectionId, slideIndex, imageSrc) {
    if (imageSrc == null || slideIndex == null) {
      return;
    }
    const selector = `.product__media-item[data-bundle-slide-index="${slideIndex}"] [data-bundle-gallery-image]`;
    [`MediaGallery-${sectionId}`, `MediaGallery-${sectionId}-duplicate`].forEach((galleryId) => {
      const gallery = document.getElementById(galleryId);
      if (!gallery) {
        return;
      }
      gallery.querySelectorAll(selector).forEach((img) => {
        img.src = imageSrc;
        img.removeAttribute("srcset");
      });
    });
  }

  function activateBundleGallerySlide(sectionId, slideIndex) {
    if (slideIndex == null) {
      return;
    }
    const mediaId = `${sectionId}-bundle-${slideIndex}`;
    [`MediaGallery-${sectionId}`, `MediaGallery-${sectionId}-duplicate`].forEach((galleryId) => {
      const gallery = document.getElementById(galleryId);
      if (!gallery) {
        return;
      }
      const activeSlide = gallery.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeSlide) {
        return;
      }
      if (typeof gallery.setActiveMedia === "function") {
        gallery.setActiveMedia(mediaId, false);
        return;
      }
      gallery.querySelectorAll(".product__media-list .product__media-item[data-media-id]").forEach((slide) => {
        const isActive = slide.getAttribute("data-media-id") === mediaId;
        slide.classList.toggle("is-active", isActive);
      });
      const activeThumbnail = gallery.querySelector(`[data-target="${mediaId}"] button`);
      gallery.querySelectorAll("[data-target] button").forEach((button) => {
        button.removeAttribute("aria-current");
      });
      if (activeThumbnail) {
        activeThumbnail.setAttribute("aria-current", "true");
      }
    });
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function moneyLabel(cents, locale, currencyCode) {
    const amount = Number(cents || 0) / 100;
    const code = currencyCode || "USD";
    try {
      const parts = new Intl.NumberFormat(locale || undefined, {
        style: "currency",
        currency: code,
        currencyDisplay: "narrowSymbol",
      }).formatToParts(amount);
      const symbol = parts.find((part) => part.type === "currency")?.value || "";
      const number = parts
        .filter((part) => part.type !== "currency")
        .map((part) => part.value)
        .join("")
        .trim();
      return `${symbol}${number}`.replace(/\s+/g, " ").trim();
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function productCardMeta(option, slot, defaultChooseLabel) {
    return {
      categoryLabel: option?.categoryLabel || slot.label || "",
      descriptionHtml: option?.productDescriptionHtml || "",
      descriptionPlain: option?.productDescription || "",
      pickerLabel: option?.pickerLabel || defaultChooseLabel,
      ingredientsHtml: option?.ingredientsHtml || "",
    };
  }

  function descriptionMarkup(meta) {
    if (meta.descriptionHtml) {
      return `<div class="custom-bundle-builder__description custom-bundle-builder__description--rich rte">${meta.descriptionHtml}</div>`;
    }
    if (meta.descriptionPlain) {
      return `<p class="custom-bundle-builder__description">${escapeHtml(meta.descriptionPlain)}</p>`;
    }
    return "";
  }

  function buildIngredientsTriggerMarkup({ panelId }) {
    return `
      <div class="custom-bundle-builder__ingredients">
        <button
          type="button"
          class="custom-bundle-builder__ingredients-trigger"
          aria-expanded="false"
          aria-controls="${escapeHtml(panelId)}"
        >
          <span class="custom-bundle-builder__ingredients-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7" r="6.25" stroke="currentColor" stroke-width="1.25"/>
              <path d="M7 6.25V9.75" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>
              <circle cx="7" cy="4.35" r="0.75" fill="currentColor"/>
            </svg>
          </span>
          <span class="custom-bundle-builder__ingredients-label">Ingredients</span>
        </button>
      </div>
    `;
  }

  let ingredientsModalTrigger = null;

  function lockIngredientsBodyScroll() {
    document.body.classList.add("overflow-hidden");
  }

  function unlockIngredientsBodyScroll() {
    document.body.classList.remove("overflow-hidden");
  }

  function hasOpenIngredientsModal(container) {
    return Boolean(
      container.querySelector(".custom-bundle-builder__ingredients-panel-wrap.is-ingredients-modal:not([hidden])"),
    );
  }

  function syncIngredientsModalLock(container, focusTarget) {
    if (hasOpenIngredientsModal(container)) {
      lockIngredientsBodyScroll();
      return;
    }
    unlockIngredientsBodyScroll();
    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(focusTarget || ingredientsModalTrigger);
    }
    ingredientsModalTrigger = null;
  }

  function openIngredientsModal(card, trigger, panelWrap, panel) {
    const closeButton = panel.querySelector(".custom-bundle-builder__ingredients-close");
    panelWrap.classList.add("is-ingredients-modal");
    card.classList.add("is-ingredients-open");
    panel.setAttribute("aria-modal", "true");
    ingredientsModalTrigger = trigger;
    lockIngredientsBodyScroll();
    if (typeof trapFocus === "function") {
      trapFocus(panel, closeButton || panel);
    }
  }

  function closeIngredientsModalState(card, panelWrap, panel) {
    panelWrap.classList.remove("is-ingredients-modal");
    card.classList.remove("is-ingredients-open");
    panel.setAttribute("aria-modal", "false");
  }

  function buildIngredientsPanelMarkup({ ingredientsHtml, panelId, productTitle, productUrl }) {
    if (!ingredientsHtml) {
      return "";
    }
    const footerLink = productUrl
      ? `<a class="custom-bundle-builder__ingredients-footer-link" href="${escapeHtml(productUrl)}">every ingredient's a hero.</a>`
      : `<span class="custom-bundle-builder__ingredients-footer-link">every ingredient's a hero.</span>`;
    return `
      <div
        id="${escapeHtml(panelId)}"
        class="custom-bundle-builder__ingredients-panel-wrap"
        hidden
      >
        <button
          type="button"
          class="custom-bundle-builder__ingredients-backdrop"
          tabindex="-1"
          aria-hidden="true"
        ></button>
        <div
          class="custom-bundle-builder__ingredients-panel"
          role="dialog"
          aria-modal="false"
          aria-label="Ingredients for ${escapeHtml(productTitle)}"
        >
          <button
            type="button"
            class="custom-bundle-builder__ingredients-close modal__close-button link link--text focus-inset"
            aria-label="Close"
          >
            <svg class="icon icon-close" aria-hidden="true" focusable="false">
              <use href="#icon-close">
            </svg>
          </button>
          <div class="custom-bundle-builder__ingredients-panel-header">
            <span class="custom-bundle-builder__ingredients-panel-kicker">Key ingredients</span>
            <span class="custom-bundle-builder__ingredients-panel-product">${escapeHtml(productTitle)}</span>
          </div>
          <div class="custom-bundle-builder__ingredients-content rte">
            ${ingredientsHtml}
          </div>
          <hr class="custom-bundle-builder__ingredients-divider">
          <p class="custom-bundle-builder__ingredients-footer">
            Nothing hidden — ${footerLink}
          </p>
        </div>
      </div>
    `;
  }

  function closeAllIngredientsPanels(container, except, options = {}) {
    const restoreFocus = options.restoreFocus !== false;
    let focusTarget = null;

    container.querySelectorAll(".custom-bundle-builder__card[data-has-ingredients]").forEach((card) => {
      if (except && card === except) {
        return;
      }
      const wrap = card.querySelector(".custom-bundle-builder__ingredients");
      const trigger = wrap?.querySelector(".custom-bundle-builder__ingredients-trigger");
      const panelWrap = card.querySelector(".custom-bundle-builder__ingredients-panel-wrap");
      const panel = panelWrap?.querySelector(".custom-bundle-builder__ingredients-panel");
      const wasOpen = wrap?.classList.contains("is-open") || panelWrap?.classList.contains("is-ingredients-modal");
      wrap?.classList.remove("is-open");
      if (trigger) {
        if (wasOpen) {
          focusTarget = trigger;
        }
        trigger.setAttribute("aria-expanded", "false");
      }
      if (panelWrap) {
        panelWrap.hidden = true;
        panelWrap.classList.remove("is-ingredients-modal");
      }
      if (panel) {
        closeIngredientsModalState(card, panelWrap, panel);
      } else {
        card.classList.remove("is-ingredients-open");
      }
    });

    syncIngredientsModalLock(container, restoreFocus ? focusTarget : null);
  }

  function openIngredientsPanel(card, container) {
    closeAllIngredientsPanels(container, card, { restoreFocus: false });
    const wrap = card.querySelector(".custom-bundle-builder__ingredients");
    const trigger = wrap?.querySelector(".custom-bundle-builder__ingredients-trigger");
    const panelWrap = card.querySelector(".custom-bundle-builder__ingredients-panel-wrap");
    const panel = panelWrap?.querySelector(".custom-bundle-builder__ingredients-panel");
    wrap?.classList.add("is-open");
    if (trigger) {
      trigger.setAttribute("aria-expanded", "true");
    }
    if (panelWrap) {
      panelWrap.hidden = false;
    }
    if (panel && trigger) {
      openIngredientsModal(card, trigger, panelWrap, panel);
    } else {
      syncIngredientsModalLock(container);
    }
  }

  function initIngredientsPanels(container) {
    if (container.dataset.ingredientsBound) {
      return;
    }
    container.dataset.ingredientsBound = "true";

    container.querySelectorAll(".custom-bundle-builder__card[data-has-ingredients]").forEach((card) => {
      const wrap = card.querySelector(".custom-bundle-builder__ingredients");
      const trigger = wrap?.querySelector(".custom-bundle-builder__ingredients-trigger");
      const panelWrap = card.querySelector(".custom-bundle-builder__ingredients-panel-wrap");
      const panel = panelWrap?.querySelector(".custom-bundle-builder__ingredients-panel");
      const backdrop = panelWrap?.querySelector(".custom-bundle-builder__ingredients-backdrop");
      const closeButton = panel?.querySelector(".custom-bundle-builder__ingredients-close");
      if (!wrap || !trigger || !panelWrap || !panel) {
        return;
      }

      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (wrap.classList.contains("is-open")) {
          closeAllIngredientsPanels(container);
          return;
        }
        openIngredientsPanel(card, container);
      });

      backdrop?.addEventListener("click", (event) => {
        event.preventDefault();
        closeAllIngredientsPanels(container);
      });

      closeButton?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeAllIngredientsPanels(container);
      });

      trigger.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          closeAllIngredientsPanels(container);
        }
      });

      panel.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          closeAllIngredientsPanels(container);
        }
      });
    });
  }

  function variantOptionLabel(option) {
    const title = option.variant?.title || "";
    const sold = isSoldOut(option.variant) ? " (sold out)" : "";
    return `${title}${sold}`;
  }

  function buildVariantListboxMarkup({
    selectionKey,
    slotId,
    displayOptions,
    defaultId,
    chooseLabel,
    productTitle,
    listboxId,
  }) {
    const defaultOption = displayOptions.find((option) => String(option.variant.id) === defaultId);
    const triggerLabel = defaultOption
      ? escapeHtml(defaultOption.variant.title)
      : "Select an option";
    const optionsMarkup = displayOptions
      .map((option) => {
        const id = String(option.variant.id);
        const sold = isSoldOut(option.variant);
        const selected = !sold && id === defaultId;
        return `
          <li class="custom-bundle-builder__select-option" role="presentation">
            <button
              type="button"
              class="custom-bundle-builder__select-option-button"
              role="option"
              data-variant-id="${escapeHtml(id)}"
              aria-selected="${selected ? "true" : "false"}"
              ${sold ? "disabled" : ""}
            >
              ${escapeHtml(variantOptionLabel(option))}
            </button>
          </li>
        `;
      })
      .join("");

    return `
      <div
        class="custom-bundle-builder__listbox"
        data-selection-key="${escapeHtml(selectionKey)}"
        data-slot-id="${escapeHtml(slotId)}"
      >
        <input
          type="hidden"
          class="custom-bundle-builder__select-input"
          data-selection-key="${escapeHtml(selectionKey)}"
          value="${escapeHtml(defaultId || "")}"
        >
        <span class="custom-bundle-builder__choose-label">${escapeHtml(chooseLabel)}</span>
        <button
          type="button"
          class="custom-bundle-builder__select-trigger"
          aria-haspopup="listbox"
          aria-expanded="false"
          aria-controls="${escapeHtml(listboxId)}"
          aria-label="Variant for ${escapeHtml(productTitle)}"
        >
          <span class="custom-bundle-builder__select-value">${triggerLabel}</span>
          <span class="custom-bundle-builder__select-caret" aria-hidden="true"></span>
        </button>
        <ul
          id="${escapeHtml(listboxId)}"
          class="custom-bundle-builder__select-list"
          role="listbox"
          aria-label="Variant for ${escapeHtml(productTitle)}"
          hidden
        >
          ${optionsMarkup}
        </ul>
      </div>
    `;
  }

  function closeAllVariantListboxes(container, except) {
    container.querySelectorAll(".custom-bundle-builder__listbox").forEach((listbox) => {
      if (except && listbox === except) {
        return;
      }
      const trigger = listbox.querySelector(".custom-bundle-builder__select-trigger");
      const menu = listbox.querySelector(".custom-bundle-builder__select-list");
      if (trigger) {
        trigger.setAttribute("aria-expanded", "false");
      }
      if (menu) {
        menu.hidden = true;
      }
      listbox.classList.remove("is-open");
      listbox.closest(".custom-bundle-builder__card")?.classList.remove("is-dropdown-open");
    });
  }

  function bindVariantListboxDocumentOutsideClick() {
    if (variantListboxDocumentOutsideBound) {
      return;
    }
    variantListboxDocumentOutsideBound = true;
    document.addEventListener(
      "click",
      (event) => {
        const listbox = event.target.closest(".custom-bundle-builder__listbox");
        document.querySelectorAll("[data-custom-bundle-slots]").forEach((slotsEl) => {
          closeAllVariantListboxes(slotsEl, listbox || undefined);
        });
      },
      true
    );
  }

  function openVariantListbox(listbox, container) {
    const trigger = listbox.querySelector(".custom-bundle-builder__select-trigger");
    const menu = listbox.querySelector(".custom-bundle-builder__select-list");
    const options = Array.from(listbox.querySelectorAll('[role="option"]:not([disabled])'));
    closeAllVariantListboxes(container, listbox);
    if (menu) {
      menu.hidden = false;
    }
    if (trigger) {
      trigger.setAttribute("aria-expanded", "true");
    }
    listbox.classList.add("is-open");
    listbox.closest(".custom-bundle-builder__card")?.classList.add("is-dropdown-open");
    const active = menu?.querySelector('[aria-selected="true"]:not([disabled])') || options[0];
    active?.focus();
  }

  function setListboxSelection(listbox, variantId, optionByVariantId) {
    const input = listbox.querySelector(".custom-bundle-builder__select-input");
    const valueEl = listbox.querySelector(".custom-bundle-builder__select-value");
    const option = optionByVariantId.get(String(variantId));
    if (input) {
      input.value = variantId || "";
    }
    if (valueEl) {
      valueEl.textContent = option ? option.variant.title : "Select an option";
    }
    listbox.querySelectorAll('[role="option"]').forEach((button) => {
      const isSelected = button.getAttribute("data-variant-id") === String(variantId);
      button.setAttribute("aria-selected", isSelected ? "true" : "false");
    });
  }

  function initVariantListboxes(container, selectedByKey, optionByVariantId, onChange) {
    container.querySelectorAll(".custom-bundle-builder__listbox").forEach((listbox) => {
      const key = listbox.getAttribute("data-selection-key");
      const trigger = listbox.querySelector(".custom-bundle-builder__select-trigger");
      const menu = listbox.querySelector(".custom-bundle-builder__select-list");
      const options = Array.from(listbox.querySelectorAll('[role="option"]:not([disabled])'));

      if (!trigger || !menu || !key) {
        return;
      }

      trigger.addEventListener("click", () => {
        const willOpen = menu.hidden;
        if (willOpen) {
          openVariantListbox(listbox, container);
        } else {
          closeAllVariantListboxes(container);
        }
      });

      menu.querySelectorAll('[role="option"]').forEach((optionButton) => {
        optionButton.addEventListener("click", () => {
          if (optionButton.disabled) {
            return;
          }
          const variantId = optionButton.getAttribute("data-variant-id");
          selectedByKey[key] = variantId;
          setListboxSelection(listbox, variantId, optionByVariantId);
          closeAllVariantListboxes(container);
          onChange();
        });
      });

      trigger.addEventListener("keydown", (event) => {
        if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (menu.hidden) {
            openVariantListbox(listbox, container);
          }
        } else if (event.key === "Escape") {
          closeAllVariantListboxes(container);
        }
      });

      menu.addEventListener("keydown", (event) => {
        const currentIndex = options.indexOf(document.activeElement);
        if (event.key === "Escape") {
          event.preventDefault();
          closeAllVariantListboxes(container);
          trigger.focus();
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          const next = options[Math.min(currentIndex + 1, options.length - 1)] || options[0];
          next?.focus();
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          const prev = options[Math.max(currentIndex - 1, 0)] || options[options.length - 1];
          prev?.focus();
        } else if (event.key === "Home") {
          event.preventDefault();
          options[0]?.focus();
        } else if (event.key === "End") {
          event.preventDefault();
          options[options.length - 1]?.focus();
        } else if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          document.activeElement?.click();
        }
      });
    });

    bindVariantListboxDocumentOutsideClick();
  }

  function clearBuilderLoadingState(root, slotsEl) {
    if (slotsEl) {
      slotsEl.removeAttribute("aria-busy");
    }
    if (root) {
      root.classList.remove("is-loading");
    }
  }

  function render(root, config, catalog, variantPrices) {
    const slotsEl = root.querySelector("[data-custom-bundle-slots]");
    const statusEl = root.querySelector("[data-custom-bundle-status]");
    const totalEls = root.querySelectorAll("[data-custom-bundle-total]");
    const valueEls = root.querySelectorAll("[data-custom-bundle-value]");
    const discountEls = root.querySelectorAll("[data-custom-bundle-discount]");
    const submitEl = root.querySelector("[data-custom-bundle-submit]");
    const submitPriceWrapEl = root.querySelector("[data-custom-bundle-submit-price-wrap]");
    const submitPriceEl = root.querySelector("[data-custom-bundle-submit-price]");
    const currencyCode = root.dataset.currency || "USD";
    const locale = document.documentElement.lang || undefined;
    const chooseLabel = root.dataset.chooseLabel || "Choose your option";
    const showPriceInButton = root.dataset.showPriceInButton !== "false";
    const showCategory = root.dataset.showCategory === "true";
    const showComponentPrice = root.dataset.showComponentPrice === "true";
    const hideStep = root.dataset.hideStep === "true";
    const sectionId = root.dataset.sectionId;
    /** @type {Record<string, string>} */
    const selectedByKey = {};

    /** One card per product (App Home: one product per slot). */
    const cards = [];
    catalog.forEach(({ slot, options }) => {
      const byHandle = new Map();
      (options || []).forEach((option) => {
        const key = option.handle || option.productTitle || String(option.variant.id);
        if (!byHandle.has(key)) {
          byHandle.set(key, []);
        }
        byHandle.get(key).push(option);
      });
      const productCount = byHandle.size;
      byHandle.forEach((productOptions, handle) => {
        cards.push({
          slot,
          handle,
          options: productOptions,
          selectionKey: productCount === 1 ? slot.id : `${slot.id}::${handle}`,
        });
      });
    });

    const buildCardMarkup = ({ slot, options, selectionKey }, index) => {
      const inStock = options.filter((option) => !isSoldOut(option.variant));
      const displayOptions = options.length ? options : [];
      const productTitle = options[0]?.productTitle || slot.label || "Product";
      const cardMeta = productCardMeta(options[0], slot, chooseLabel);
      const categoryLabel = cardMeta.categoryLabel;
      const descriptionMarkupHtml = descriptionMarkup(cardMeta);
      const cardPickerLabel = cardMeta.pickerLabel;
      const ingredientsPanelId = `CustomBundleIngredients-${sectionId}-${index}`.replace(/[^a-zA-Z0-9_-]/g, "-");
      const productUrl = options[0]?.productUrl || "";
      const hasIngredients = Boolean(cardMeta.ingredientsHtml);
      const ingredientsTriggerMarkup = hasIngredients
        ? buildIngredientsTriggerMarkup({ panelId: ingredientsPanelId })
        : "";
      const ingredientsPanelMarkup = hasIngredients
        ? buildIngredientsPanelMarkup({
            ingredientsHtml: cardMeta.ingredientsHtml,
            panelId: ingredientsPanelId,
            productTitle,
            productUrl,
          })
        : "";
      const cardAttributes = hasIngredients ? ' data-has-ingredients="true"' : "";

      const stepMarkup = hideStep
        ? ""
        : `<div class="custom-bundle-builder__step">${index + 1}</div>`;

      const buildCardHeaderMarkup = (categoryText, selectionKey, priceText) => {
        const categoryHtml =
          showCategory && categoryText
            ? `<p class="custom-bundle-builder__category">${escapeHtml(categoryText)}</p>`
            : "";
        const priceHtml =
          showComponentPrice && priceText
            ? `<strong class="custom-bundle-builder__price" data-price-for="${escapeHtml(selectionKey)}">${priceText}</strong>`
            : "";
        if (!categoryHtml && !priceHtml) {
          return "";
        }
        return `<div class="custom-bundle-builder__card-header">${categoryHtml}${priceHtml}</div>`;
      };

      if (!inStock.length) {
        delete selectedByKey[selectionKey];
        const preview = options[0];
        const imageHtml = preview ? optionImage(preview) : "";
        return `
          <article class="custom-bundle-builder__card"${cardAttributes} data-selection-key="${escapeHtml(selectionKey)}" data-bundle-slide-index="${index}" data-sold-out="true">
            <div class="custom-bundle-builder__card-inner">
              <div class="custom-bundle-builder__media-wrap">
                ${stepMarkup}
                <div class="custom-bundle-builder__media">${imageHtml}</div>
              </div>
              <div class="custom-bundle-builder__card-body">
                ${buildCardHeaderMarkup(categoryLabel, selectionKey, "")}
                <div class="custom-bundle-builder__title-row">
                  <h3 class="custom-bundle-builder__product-title">${escapeHtml(productTitle)}</h3>
                  ${ingredientsTriggerMarkup}
                </div>
                ${descriptionMarkupHtml}
                <p class="custom-bundle-builder__hint">Sold out — this product cannot be added to the bundle.</p>
              </div>
            </div>
            ${ingredientsPanelMarkup}
          </article>
        `;
      }

      const defaultChoice = inStock[0];
      const defaultId = String(defaultChoice.variant.id);
      selectedByKey[selectionKey] = defaultId;
      const imageHtml = optionImage(defaultChoice);
      const priceHtml = moneyLabel(Number(defaultChoice.variant.price || 0), locale, currencyCode);
      const titleLine = formatTitleLine(defaultChoice);
      const single = inStock.length === 1;

      let variantControl = "";
      if (single) {
        const only = inStock[0];
        const label =
          only.variant.title && only.variant.title !== "Default Title"
            ? escapeHtml(only.variant.title)
            : "";
        variantControl = label ? `<p class="custom-bundle-builder__fixed-variant">${label}</p>` : "";
      } else {
        const listboxId = `CustomBundleListbox-${sectionId}-${index}`.replace(/[^a-zA-Z0-9_-]/g, "-");
        variantControl = `
          <div class="custom-bundle-builder__choose">
            ${buildVariantListboxMarkup({
              selectionKey,
              slotId: slot.id,
              displayOptions,
              defaultId,
              chooseLabel: cardPickerLabel,
              productTitle: inStock[0].productTitle,
              listboxId,
            })}
          </div>
        `;
      }

      return `
        <article class="custom-bundle-builder__card"${cardAttributes} data-selection-key="${escapeHtml(selectionKey)}" data-slot-id="${escapeHtml(slot.id)}" data-bundle-slide-index="${index}">
          <div class="custom-bundle-builder__card-inner">
            <div class="custom-bundle-builder__media-wrap">
              ${stepMarkup}
              <div class="custom-bundle-builder__media">${imageHtml}</div>
            </div>
            <div class="custom-bundle-builder__card-body">
              ${buildCardHeaderMarkup(categoryLabel, selectionKey, priceHtml)}
              <div class="custom-bundle-builder__title-row">
                <h3 class="custom-bundle-builder__product-title" data-title-for="${escapeHtml(selectionKey)}">${escapeHtml(titleLine)}</h3>
                ${ingredientsTriggerMarkup}
              </div>
              ${descriptionMarkupHtml}
              ${variantControl}
            </div>
          </div>
          ${ingredientsPanelMarkup}
        </article>
      `;
    };

    slotsEl.innerHTML = cards.map(buildCardMarkup).join("");
    clearBuilderLoadingState(root, slotsEl);

    const optionByVariantId = new Map();
    cards.forEach(({ options }) => {
      options.forEach((option) => {
        optionByVariantId.set(String(option.variant.id), option);
      });
    });

    const sync = (options = {}) => {
      const preserveAtcError = Boolean(options.preserveAtcError && root.dataset.atcError);
      slotsEl.querySelectorAll(".custom-bundle-builder__select-input[data-selection-key]").forEach((input) => {
        const key = input.getAttribute("data-selection-key");
        const listbox = input.closest(".custom-bundle-builder__listbox");
        if (input.value) {
          selectedByKey[key] = input.value;
        }
        let selectedOption = optionByVariantId.get(String(selectedByKey[key]));
        if (selectedOption && isSoldOut(selectedOption.variant)) {
          const fallback = cards
            .find((card) => card.selectionKey === key)
            ?.options.find((option) => !isSoldOut(option.variant));
          if (fallback) {
            selectedByKey[key] = String(fallback.variant.id);
            if (listbox) {
              setListboxSelection(listbox, selectedByKey[key], optionByVariantId);
            } else {
              input.value = selectedByKey[key];
            }
          } else {
            delete selectedByKey[key];
            if (listbox) {
              setListboxSelection(listbox, "", optionByVariantId);
            } else {
              input.value = "";
            }
          }
        }
        selectedOption = optionByVariantId.get(String(selectedByKey[key]));
        const priceEl = slotsEl.querySelector(`[data-price-for="${key}"]`);
        if (priceEl && selectedByKey[key]) {
          const cents = variantPrices[String(selectedByKey[key])];
          if (typeof cents === "number") {
            priceEl.textContent = moneyLabel(cents, locale, currencyCode);
          }
        }
        const titleEl = slotsEl.querySelector(`[data-title-for="${key}"]`);
        if (titleEl && selectedOption) {
          titleEl.textContent = formatTitleLine(selectedOption);
        }
        const cardEl = slotsEl.querySelector(`[data-selection-key="${key}"]`);
        if (cardEl && selectedOption) {
          const mediaEl = cardEl.querySelector(".custom-bundle-builder__media");
          if (mediaEl) {
            mediaEl.innerHTML = optionImage(selectedOption);
          }
        }
      });

      let valueCents = 0;
      cards.forEach(({ selectionKey, options }) => {
        const variantId = selectedByKey[selectionKey];
        const selected = optionByVariantId.get(String(variantId));
        if (!variantId || (selected && isSoldOut(selected.variant))) {
          return;
        }
        if (typeof variantPrices[variantId] === "number") {
          valueCents += variantPrices[variantId];
        }
      });

      cards.forEach(({ selectionKey }, index) => {
        const variantId = selectedByKey[selectionKey];
        const selected = optionByVariantId.get(String(variantId));
        if (selected) {
          updateBundleGalleryImage(sectionId, index, optionGalleryImage(selected));
        }
      });

      const cardIssues = [];
      const soldOutNames = [];
      cards.forEach(({ selectionKey, options, slot }) => {
        const title = options[0]?.productTitle || slot.label || "Product";
        const inStock = options.filter((option) => !isSoldOut(option.variant));
        if (!options.length || !inStock.length) {
          soldOutNames.push(title);
          cardIssues.push(`${title} is sold out.`);
          return;
        }
        const selected = optionByVariantId.get(String(selectedByKey[selectionKey]));
        if (!selectedByKey[selectionKey] || (selected && isSoldOut(selected.variant))) {
          cardIssues.push(`${title} needs an in-stock variant.`);
        }
      });

      let totalCents = 0;
      cards.forEach(({ selectionKey, options }) => {
        const variantId = selectedByKey[selectionKey];
        const selected = optionByVariantId.get(String(variantId));
        if (!variantId || (selected && isSoldOut(selected.variant))) {
          return;
        }
        if (typeof variantPrices[variantId] === "number") {
          totalCents += variantPrices[variantId];
        }
      });
      const discount = Number(config.pricing?.percentageDecrease || 0);
      if (discount > 0) {
        totalCents = Math.round(totalCents * (1 - discount / 100));
      }

      if (!root.dataset.bundleJustAdded && !preserveAtcError) {
        statusEl.textContent =
          soldOutNames.length > 0
            ? `Sold out: ${soldOutNames.join(", ")}. Remove them from the bundle or restock for Online Store.`
            : cardIssues[0] || "";
        statusEl.dataset.tone = cardIssues.length ? "error" : "ok";
      }

      const percent =
        valueCents > 0 && totalCents < valueCents
          ? Math.round(((valueCents - totalCents) / valueCents) * 100)
          : 0;

      if (valueEls.length) {
        const showValue = valueCents > 0 && totalCents < valueCents;
        const valueText = showValue ? bundleTotalLabel(valueCents, locale, currencyCode) : "";
        valueEls.forEach((el) => {
          el.textContent = valueText;
          el.hidden = !showValue;
        });
      }

      const totalText = bundleTotalLabel(totalCents, locale, currencyCode);

      if (totalEls.length) {
        totalEls.forEach((el) => {
          el.textContent = totalText;
        });
      }

      if (showPriceInButton && submitPriceEl && submitPriceWrapEl) {
        submitPriceEl.textContent = totalText;
        submitPriceWrapEl.hidden = false;
      } else if (submitPriceWrapEl) {
        submitPriceWrapEl.hidden = true;
      }

      if (discountEls.length) {
        const showDiscount = percent > 0;
        const discountText = showDiscount ? `${percent}% OFF` : "";
        discountEls.forEach((el) => {
          el.textContent = discountText;
          el.hidden = !showDiscount;
        });
      }

      submitEl.disabled = cardIssues.length > 0;
      return { issues: cardIssues, soldOutNames };
    };

    initVariantListboxes(slotsEl, selectedByKey, optionByVariantId, () => {
      delete root.dataset.bundleJustAdded;
      delete root.dataset.atcError;
      sync();
    });
    initIngredientsPanels(slotsEl);

    root.addEventListener("click", (event) => {
      if (
        !event.target.closest(".custom-bundle-builder__ingredients") &&
        !event.target.closest(".custom-bundle-builder__ingredients-panel-wrap")
      ) {
        closeAllIngredientsPanels(slotsEl);
      }
    });

    slotsEl.addEventListener("focusin", (event) => {
      const card = event.target.closest("[data-bundle-slide-index]");
      if (!card) {
        return;
      }
      activateBundleGallerySlide(sectionId, Number(card.getAttribute("data-bundle-slide-index")));
    });
    sync();

    submitEl.addEventListener("click", async () => {
      delete root.dataset.bundleJustAdded;
      delete root.dataset.atcError;
      const { issues } = sync();
      if (issues.length) {
        statusEl.textContent = issues[0];
        statusEl.dataset.tone = "error";
        return;
      }
      submitEl.disabled = true;
      statusEl.textContent = "Adding bundle to cart…";
      statusEl.dataset.tone = "ok";
      try {
        const instanceId = createInstanceId();
        const selections = {};
        cards.forEach(({ slot, selectionKey }) => {
          const variantId = selectedByKey[selectionKey];
          const selected = optionByVariantId.get(String(variantId));
          if (!variantId || (selected && isSoldOut(selected.variant))) {
            return;
          }
          const id = String(variantNumericId(variantId));
          if (!id) {
            return;
          }
          if (!selections[slot.id]) {
            selections[slot.id] = {};
          }
          selections[slot.id][id] = Number(selections[slot.id][id] || 0) + 1;
        });
        const payload = buildCartPayload(config, selections, instanceId);
        if (!payload.items.length) {
          throw new Error("empty_payload");
        }
        const result = await postCart(payload);
        root.dispatchEvent(
          new CustomEvent(EVENT_NAME, {
            bubbles: true,
            detail: { instanceId, items: payload.items, result },
          }),
        );
        refreshThemeCart(result);
        delete root.dataset.atcError;
        root.dataset.bundleJustAdded = "1";
        statusEl.textContent = "Bundle added to cart.";
        statusEl.dataset.tone = "ok";
      } catch (error) {
        console.error("[custom-bundle] add to cart failed", error);
        const message = error instanceof Error ? error.message : String(error || "");
        root.dataset.atcError = "1";
        if (message.startsWith("password_gate:")) {
          statusEl.textContent =
            "Could not add to cart — storefront password is blocking cart Ajax. Enter the store password at /password, then try again.";
        } else if (message === "empty_payload") {
          statusEl.textContent = "Could not build cart lines for this bundle.";
        } else {
          statusEl.textContent = message.startsWith("Could not add")
            ? message
            : parseCartAddError(message);
        }
        statusEl.dataset.tone = "error";
      } finally {
        submitEl.disabled = false;
        const current = sync({ preserveAtcError: true });
        if (root.dataset.bundleJustAdded && !current.issues.length) {
          statusEl.textContent = "Bundle added to cart.";
          statusEl.dataset.tone = "ok";
        }
      }
    });
  }

  function statusForLoadFailure({ failedHandles, passwordBlocked, hasOptions }) {
    if (passwordBlocked && !hasOptions) {
      return PASSWORD_HINT;
    }
    if (failedHandles.length && !hasOptions) {
      return `Unable to load live variant prices for: ${failedHandles.join(", ")}.`;
    }
    if (failedHandles.length && hasOptions) {
      return `Some products failed to load: ${failedHandles.join(", ")}.`;
    }
    if (!hasOptions) {
      return "Unable to load live variant prices.";
    }
    return "";
  }

  async function initRoot(root) {
    if (!root || root.dataset.customBundleInit) {
      return;
    }
    root.dataset.customBundleInit = "1";
    const sectionId = root.dataset.sectionId;
    const slotsEl = root.querySelector("[data-custom-bundle-slots]");
    const configScript =
      root.querySelector(`#CustomBundleConfig-${sectionId}`) ||
      document.getElementById(`CustomBundleConfig-${sectionId}`);
    const catalogScript =
      root.querySelector(`#CustomBundleCatalog-${sectionId}`) ||
      document.getElementById(`CustomBundleCatalog-${sectionId}`);
    const config = parseConfig(configScript ? configScript.textContent : "");
    const failInit = () => {
      if (slotsEl) {
        slotsEl.innerHTML = "";
      }
      clearBuilderLoadingState(root, slotsEl);
    };
    if (!config) {
      failInit();
      return;
    }
    const statusEl = root.querySelector("[data-custom-bundle-status]");
    const embedded = parseConfig(catalogScript ? catalogScript.textContent : "");
    const fromLiquid = catalogFromEmbedded(config, embedded);
    if (fromLiquid) {
      render(root, config, fromLiquid.catalog, fromLiquid.variantPrices);
      return;
    }

    try {
      const loaded = await loadSlotCatalog(config);
      const hasOptions = catalogHasOptions(loaded.catalog);
      if (hasOptions) {
        render(root, config, loaded.catalog, loaded.variantPrices);
        const warning = statusForLoadFailure({
          failedHandles: loaded.failedHandles,
          passwordBlocked: loaded.passwordBlocked,
          hasOptions: true,
        });
        if (warning && statusEl) {
          statusEl.textContent = warning;
          statusEl.dataset.tone = "error";
        }
        return;
      }
      failInit();
      if (statusEl) {
        statusEl.textContent = statusForLoadFailure({
          failedHandles: loaded.failedHandles,
          passwordBlocked: loaded.passwordBlocked,
          hasOptions: false,
        });
        statusEl.dataset.tone = "error";
      }
    } catch (error) {
      console.error("[custom-bundle] failed to load live prices", error);
      failInit();
      if (statusEl) {
        const message = error instanceof Error ? error.message : String(error || "");
        statusEl.textContent = message.startsWith("password_gate:")
          ? PASSWORD_HINT
          : "Unable to load live variant prices.";
        statusEl.dataset.tone = "error";
      }
    }
  }

  function init(documentRef) {
    const doc = documentRef || document;
    if (!doc || typeof doc.querySelectorAll !== "function") {
      return;
    }
    doc.querySelectorAll("[data-custom-bundle-root]").forEach((root) => {
      initRoot(root);
    });
  }

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => init(document));
    } else {
      init(document);
    }
    document.addEventListener("shopify:section:load", (event) => {
      init(event.target);
    });
  }

  return {
    EVENT_NAME,
    parseConfig,
    moneyFromCents,
    variantNumericId,
    createInstanceId,
    filterVariants,
    isSoldOut,
    slotSelectionIssues,
    validateSelections,
    componentSumCents,
    buildCartPayload,
    fetchProductJson,
    loadSlotCatalog,
    catalogFromEmbedded,
    postCart,
    init,
  };
});
